import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Components
import Layout from './components/Layout';
import OnboardingGuard from './components/OnboardingGuard';
import DashboardRouter from './components/DashboardRouter';
// Auth pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyEmail from './pages/auth/VerifyEmail';

// Legal pages
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';

// Onboarding
import OnboardingWrapper from './pages/onboarding/OnboardingWrapper';

// Client pages
// Note: ClientDashboard is imported in DashboardRouter
// import ClientDashboard from './pages/client/Dashboard';
import ClientWorkouts from './pages/client/Workouts';
import ClientWorkoutDetail from './pages/client/WorkoutDetail';
import ClientMeals from './pages/client/Meals';
import ClientMealDetail from './pages/client/MealDetail';
import ClientProgress from './pages/client/Progress';

// Trainer pages
import TrainerDashboard from './pages/trainer/Dashboard';
import TrainerClients from './pages/trainer/Clients';
import TrainerClientDetail from './pages/trainer/ClientDetail';
import TrainerWorkoutBuilder from './pages/trainer/WorkoutBuilder';
import TrainerWorkouts from './pages/trainer/Workouts';
import TrainerMealPlanBuilder from './pages/trainer/MealPlanBuilder';

// Consultant pages
import ConsultantDashboard from './pages/consultant/Dashboard';
import ConsultantUserManagement from './pages/consultant/UserManagement';

// Shared pages
import SharedProfile from './pages/shared/Profile';
import SharedLibraries from './pages/shared/Libraries';
import SharedExerciseLibrary from './pages/shared/libraries/ExerciseLibrary';
import SharedEquipmentLibrary from './pages/shared/libraries/EquipmentLibrary';
import SharedFoodLibrary from './pages/shared/libraries/FoodLibrary';
import SharedExercises from './pages/shared/Exercises';
import SharedEquipment from './pages/shared/Equipment';

// Hooks
import { useAuthStore } from './stores/authStore';

// Styles
import './index.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { isAuthenticated } = useAuthStore();

  return (
    <QueryClientProvider client={queryClient}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <div className="App">
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
          
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" />} />
            <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to="/dashboard" />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
            <Route path="/reset-password" element={!isAuthenticated ? <ResetPassword /> : <Navigate to="/dashboard" />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            
            {/* Protected routes */}
            <Route path="/" element={isAuthenticated ? <Layout /> : <Navigate to="/login" />}>
              <Route index element={<Navigate to="/dashboard" />} />
              {/* Dashboard - role-specific */}
              <Route path="dashboard" element={
                <OnboardingGuard>
                  <DashboardRouter />
                </OnboardingGuard>
              } />
              
              {/* Client routes */}
              <Route path="client/workouts" element={
                <OnboardingGuard>
                  <ClientWorkouts />
                </OnboardingGuard>
              } />
              <Route path="client/workouts/:id" element={
                <OnboardingGuard>
                  <ClientWorkoutDetail />
                </OnboardingGuard>
              } />
              <Route path="client/meals" element={
                <OnboardingGuard>
                  <ClientMeals />
                </OnboardingGuard>
              } />
              <Route path="client/meals/:id" element={
                <OnboardingGuard>
                  <ClientMealDetail />
                </OnboardingGuard>
              } />
              <Route path="client/progress" element={
                <OnboardingGuard>
                  <ClientProgress />
                </OnboardingGuard>
              } />
              
              {/* Trainer routes */}
              <Route path="trainer/dashboard" element={<TrainerDashboard />} />
              <Route path="trainer/clients" element={<TrainerClients />} />
              <Route path="trainer/clients/:id" element={<TrainerClientDetail />} />
              <Route path="trainer/workout-builder" element={<TrainerWorkoutBuilder />} />
              <Route path="trainer/workouts" element={<TrainerWorkouts />} />
              <Route path="trainer/meal-builder" element={<TrainerMealPlanBuilder />} />
              
              {/* Consultant routes */}
              <Route path="consultant/dashboard" element={<ConsultantDashboard />} />
              <Route path="consultant/users" element={<ConsultantUserManagement />} />
              
              {/* Shared routes */}
              <Route path="profile" element={
                <OnboardingGuard>
                  <SharedProfile />
                </OnboardingGuard>
              } />
              <Route path="exercises" element={<SharedExercises />} />
              <Route path="equipment" element={<SharedEquipment />} />
              <Route path="libraries" element={<SharedLibraries />} />
              <Route path="libraries/exercises" element={<SharedExerciseLibrary />} />
              <Route path="libraries/equipment" element={<SharedEquipmentLibrary />} />
              <Route path="libraries/foods" element={<SharedFoodLibrary />} />
              
              {/* Legacy routes for backward compatibility */}
              <Route path="clients" element={<TrainerClients />} />
              <Route path="clients/:id" element={<TrainerClientDetail />} />
              <Route path="workouts" element={<TrainerWorkouts />} />
              <Route path="workouts/:id" element={<ClientWorkoutDetail />} />
              <Route path="meals" element={<ClientMeals />} />
              <Route path="progress" element={<ClientProgress />} />
              <Route path="admin/users" element={<ConsultantUserManagement />} />
            </Route>
            
            {/* Onboarding route - accessible without Layout */}
            <Route path="/onboarding" element={isAuthenticated ? <OnboardingWrapper /> : <Navigate to="/login" />} />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
